import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ProjectEntitlement } from '../../lib/api.js';
import { Callout } from '../../components/Callout.js';
import { QueryState } from '../../components/QueryState.js';
import { PaymentPanel, PlanChooser, money, termLabel } from '../../components/Plans.js';
import { ProjectCreditsPanel } from '../../components/credits/ProjectCreditsPanel.js';


/**
 * Paying for this production.
 *
 * WHAT THIS SCREEN HAS TO GET RIGHT is the difference between a production that
 * has never been paid for and one whose period has ended. Both run on Free;
 * only the second is read-only, and a producer who has just been stopped
 * mid-edit needs the way back rather than an explanation.
 */
export function BillingTab({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<{ code: string; term: number } | null>(null);

  const state = useQuery({
    queryKey: ['billing', projectId],
    queryFn: () => api.billingState(projectId),
  });

  const plans = useQuery({
    queryKey: ['billing-plans', projectId],
    queryFn: () => api.billingPlans(projectId),
  });

  const entitlement = state.data;

  return (
    <div data-testid="billing-tab">
      <p className="dh" style={{ fontSize: 22 }}>
        Plan &amp; billing
      </p>

      {/* The entitlement decides what the whole screen says, so its own
          failure must not read as "this production is on Free". */}
      <QueryState query={state} subject="this production's plan" testId="billing-state" />
      <QueryState query={plans} subject="the plans" testId="billing-plans" />

      {entitlement ? (
        <CurrentState entitlement={entitlement} aiPerUse={(plans.data?.plans ?? []).some((plan) => plan.aiPerUse === true)} />
      ) : null}

      {/* PROJECT AI CREDITS (S4). Hidden before launch (LEGACY), so this tab is
          exactly what it was until metered AI is switched on. */}
      <ProjectCreditsPanel projectId={projectId} readOnly={entitlement?.readOnly ?? false} />

      {/* THE SAME COMPARISON THE PRODUCER AGREED TO. This tab used to draw
          its own stack of cards, so the prices somebody accepted while
          creating a production and the prices they read afterwards came from
          two pieces of code with no reason to agree. */}
      <PlanChooser
        plans={plans.data?.plans ?? []}
        chosen={chosen}
        onChoose={setChosen}
        currentPlanCode={entitlement?.state === 'PAID' ? entitlement.planCode : 'FREE'}
      />

      {chosen ? (
        <div
          data-testid="billing-purchase-summary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
            marginTop: 20,
            paddingTop: 18,
            borderTop: '1px solid var(--color-divider)',
          }}
        >
          <p className="mm" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.6 }}>
            {(() => {
              const plan = plans.data?.plans.find((p) => p.code === chosen.code);
              const price = plan?.prices.find((p) => p.termMonths === chosen.term);

              if (!plan || !price) return 'Choose a billing period to continue.';

              return `${plan.name} · ${termLabel(price.termMonths).toLowerCase()} — ${money(price.amountMinor, price.currency)} billed once.`;
            })()}
            {entitlement?.state === 'PAID'
              ? ' Paying again before the current period ends adds to the time you already have.'
              : ''}
          </p>
        </div>
      ) : null}

      {chosen ? (
        <PaymentPanel
          projectId={projectId}
          planCode={chosen.code}
          termMonths={chosen.term}
          onCancel={() => {
            setChosen(null);
          }}
          onPaid={async () => {
            setChosen(null);
            // EVERYTHING ON THIS PRODUCTION is stale the moment a payment lands
            // — the entitlement gates read-only state and nine features, so a
            // paid production renders differently on every tab, not just this
            // one. Scoped by predicate rather than by listing the keys, because
            // a list would silently stop covering a tab added later.
            //
            // It is scoped to THIS project rather than invalidating the whole
            // client: another production's cache is not affected by this
            // payment, and refetching it would be work nobody asked for.
            await queryClient.invalidateQueries({
              predicate: (query) => query.queryKey.includes(projectId),
            });
            // The list carries each production's plan badge, and its key does
            // not name a project.
            await queryClient.invalidateQueries({ queryKey: ['projects'] });
          }}
        />
      ) : null}
    </div>
  );
}

function CurrentState({ entitlement, aiPerUse }: { entitlement: ProjectEntitlement; aiPerUse: boolean }) {
  const ends = entitlement.periodEndsAt === null ? null : new Date(entitlement.periodEndsAt);
  const endsLabel = ends === null ? null : ends.toLocaleDateString('en-GB');

  if (entitlement.state === 'EXPIRED') {
    return (
      <div style={{ marginTop: 14 }}>
        <Callout tone="danger" title="This production is read-only" testId="billing-expired">
          The paid period ended{endsLabel === null ? '' : ` on ${endsLabel}`}. Everything is still
          here — every scene, requirement and candidate — and nothing will be deleted. Choose a plan
          below to start editing again.
        </Callout>
      </div>
    );
  }

  if (entitlement.state === 'FREE') {
    return (
      <div style={{ marginTop: 14 }}>
        <Callout tone="ok" title="On the free plan" testId="billing-free">
          {aiPerUse
            ? 'Location management is included, and AI runs are paid per use from this production’s credits on every plan. Market data, storage and the collaboration module need a paid plan.'
            : 'Location management is included. The AI scout, market data, storage and the collaboration module need a paid plan.'}
        </Callout>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <Callout
        tone="ok"
        title={`Paid — ${entitlement.planCode}`}
        testId="billing-paid"
      >
        {endsLabel === null
          ? 'This production is active.'
          : `Active until ${endsLabel}. Paying again before then adds to the time you already have, rather than replacing it.`}
      </Callout>
    </div>
  );
}
